import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, Loader2, AlertCircle } from "lucide-react";
import { erp } from "@/lib/erp";
import { useChildTable } from "./useChildTable";
import { useServerFetch } from "./useServerFetch";
import { SERVER_FETCH_CONFIG } from "./serverFetchConfig";
import { ChildTableHeader } from "./ChildTableHeader";
import { ChildTableRow } from "./ChildTableRow";
import { ChildRowEditModal } from "./ChildRowEditModal";
import { validateChildTable, formatValidationErrors } from "./utils/validation";
import { getVisibleChildTableFields } from "./utils/schemaUtils";
import type { ChildTableFieldProps, RowValidationError, ChildTableRow as ChildTableRowType } from "./types";
import { cn } from "@/lib/utils";

/**
 * Main component that renders a complete child table for a Table field.
 * Supports server fetch: when a trigger field changes (e.g., item_code),
 * calls a server method and patches the row with dependent field values.
 */
export function ChildTableField({
  field,
  value,
  onChange,
  disabled,
  resetKey,
  viewMode = 'experience',
  parentDoctype,
  parentDoc,
}: ChildTableFieldProps) {
  // Fetch child doctype schema - field.options contains child doctype name
  const childDoctype = field.options || "";

  const { data: childSchema, isLoading, error } = useQuery({
    queryKey: ["meta", childDoctype],
    queryFn: () => erp.meta.getDocTypeSchema(childDoctype),
    enabled: !!childDoctype,
  });

  // Determine if we should use in_list_view filtering (System Mode only)
  const shouldUseListViewColumns = viewMode === 'system';

  // Filter visible fields for grid columns
  const visibleFields = useMemo(() => {
    if (!childSchema) return [];

    const allNonSystemFields = getVisibleChildTableFields(childSchema);

    if (shouldUseListViewColumns) {
      const listViewFields = allNonSystemFields.filter((f) => {
        const v = (f as any).in_list_view;
        return v === 1 || v === true || v === "1";
      });

      if (process.env.NODE_ENV === 'development' && listViewFields.length === 0) {
        console.warn(
          '[ChildTableField] No in_list_view fields configured for child doctype',
          childSchema.name,
          '– falling back to first 4 fields in grid.'
        );
      }

      if (listViewFields.length > 0) {
        return listViewFields;
      }

      const gridCandidates = allNonSystemFields.filter(
        (f) => !['Text Editor', 'Long Text', 'Small Text'].includes(f.fieldtype)
      );

      const fallbackFields = (gridCandidates.length > 0 ? gridCandidates : allNonSystemFields).slice(0, 4);
      return fallbackFields;
    } else {
      return allNonSystemFields;
    }
  }, [childSchema, shouldUseListViewColumns]);

  // Normalize value to array before passing into useChildTable
  const normalizedValue = Array.isArray(value) ? value : [];

  // Use child table hook for state management with resetKey
  const { rows, addRow, deleteRow, duplicateRow, updateRow } = useChildTable({
    initialRows: normalizedValue,
    childFields: visibleFields,
    onChange,
    resetKey,
  });

  // Server fetch: detect trigger field changes and auto-populate dependent fields
  const serverFetchConfig = parentDoctype
    ? SERVER_FETCH_CONFIG[parentDoctype]
    : undefined;
  const { handleRowChange, fetchStates } = useServerFetch({
    config: serverFetchConfig,
    parentDoc,
    parentDoctype,
    updateRow,
    rows,
  });

  // Extract trigger field names for shimmer exclusion and validation skip
  const triggerFields = useMemo(
    () => serverFetchConfig?.map((c) => c.triggerField) || [],
    [serverFetchConfig]
  );

  // ALL HOOKS MUST BE BEFORE ANY EARLY RETURNS
  // Validation state
  const [validationErrors, setValidationErrors] = useState<RowValidationError[]>([]);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);

  // Validate rows whenever they change (synchronous - fast)
  useEffect(() => {
    if (visibleFields.length > 0 && rows.length > 0) {
      const errors = validateChildTable(
        rows,
        visibleFields,
        field.label,
        triggerFields.length > 0 ? triggerFields : undefined
      );
      setValidationErrors(errors);
    } else {
      setValidationErrors([]);
    }
  }, [rows, visibleFields, field.label, triggerFields]);

  // Helper functions (non-hooks, safe to define after hooks)
  const getRowErrors = (rowIndex: number) => {
    return validationErrors.filter((err) => err.rowIndex === rowIndex);
  };

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setModalOpen(true);
  };

  const handleModalOpenChange = (open: boolean) => {
    setModalOpen(open);
    if (!open) {
      setEditingRowIndex(null);
    }
  };

  const handleModalRowChange = (updatedRow: ChildTableRowType) => {
    if (editingRowIndex !== null) {
      updateRow(editingRowIndex, updatedRow);

      // Synchronously notify parent of the change so MetaFieldEditModal's
      // localFormData updates immediately (not just via useEffect).
      // This prevents computePatch() from seeing stale data when the user
      // clicks Save right after closing ChildRowEditModal.
      if (onChange) {
        const merged = { ...rows[editingRowIndex], ...updatedRow };
        const updatedRows = rows.map((row, i) =>
          i === editingRowIndex ? merged : row
        );
        const cleanRows = updatedRows
          .filter((row) => !row.__deleted)
          .map(({ __state, __originalData, __deleted, _localId, ...clean }) => clean);
        onChange(cleanRows);
      }
    }
    handleModalOpenChange(false);
  };

  const handleModalDelete = () => {
    if (editingRowIndex !== null) {
      deleteRow(editingRowIndex);
    }
    handleModalOpenChange(false);
  };

  const editingRow = editingRowIndex !== null ? rows[editingRowIndex] : null;

  // Determine if there are any non-deleted rows to display
  const hasVisibleRows = rows.some((row) => !row.__deleted);

  // NOW early returns are safe - after all hooks
  if (isLoading) {
    return (
      <div className="space-y-3">
        <Label>{field.label}</Label>
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Loading table schema...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3">
        <Label>{field.label}</Label>
        <div className="text-destructive text-sm py-4">
          Failed to load table schema: {error instanceof Error ? error.message : "Unknown error"}
        </div>
      </div>
    );
  }

  if (!visibleFields || visibleFields.length === 0) {
    return (
      <div className="space-y-3">
        <Label>{field.label}</Label>
        <div className="text-muted-foreground text-sm py-4">
          No fields available for this table
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Label>
        {field.label}
        {field.reqd === 1 && <span className="text-destructive ml-1">*</span>}
      </Label>

      {/* Show validation errors */}
      {validationErrors.length > 0 && (
        <div className={cn(
          "flex items-start gap-2 p-3 rounded-xs bg-destructive/10 border border-destructive/20 text-sm"
        )}>
          <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
          <div className="text-destructive">
            {formatValidationErrors(validationErrors, field.label)}
          </div>
        </div>
      )}

      <div className="border rounded-sm overflow-hidden bg-card">
        <ChildTableHeader fields={visibleFields} />
        <div className="divide-y">
          {!hasVisibleRows ? (
            <div className="px-4 py-8 text-center text-muted-foreground text-sm">
              No rows. Click "Add Row" to create one.
            </div>
          ) : (
            rows.map((row, index) => {
              if (row.__deleted) {
                return null;
              }

              const rowErrors = getRowErrors(index);
              const rowKey = row.name || row._localId || `row-${index}`;
              const rowId = row.name || row._localId || '';
              return (
              <ChildTableRow
                key={rowKey}
                row={row}
                rowIndex={index}
                fields={visibleFields}
                onRowChange={(patch) => handleRowChange(index, patch)}
                onEdit={() => handleEditRow(index)}
                onDelete={() => deleteRow(index)}
                onDuplicate={() => duplicateRow(index)}
                disabled={disabled}
                errors={rowErrors}
                fetchState={fetchStates.get(rowId)}
                triggerFields={triggerFields.length > 0 ? triggerFields : undefined}
              />
              );
            })
          )}
        </div>
      </div>
      <Button
        onClick={addRow}
        disabled={disabled}
        variant="outline"
        size="sm"
        type="button"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Row
      </Button>

      {/* Row Edit Modal */}
      {editingRow && childSchema && (
        <ChildRowEditModal
          open={modalOpen}
          onOpenChange={handleModalOpenChange}
          rowData={editingRow}
          onRowChange={handleModalRowChange}
          childMeta={childSchema}
          disabled={disabled}
          resetKey={resetKey}
          onDelete={handleModalDelete}
          parentDoc={parentDoc}
        />
      )}
    </div>
  );
}
